class SamplesForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.checkboxes = this.form.querySelectorAll('input[type="checkbox"]');
    this.spinner = this.form.querySelector('.loading-overlay__spinner');
    
    this.samplesDrawer = document.querySelector(
      'samples-drawer'
    );
    this.submitButton = this.samplesDrawer.querySelector('[type="submit"]');
    this.errorMessageWrapper = this.samplesDrawer.querySelector(
      '#product-form__error-message-wrapper'
    );
    this.errorMessage = this.errorMessageWrapper.querySelector('#product-form__error-message');
    this.errorMessageTimeout = 3000;
    this.cart = document.querySelector('cart-drawer');
    this.maxSelections = 2;
    this.samplesDrawerTrigger = document.querySelector('samples-drawer-trigger');

    this.init();
    
    // Listen for cart updates to validate samples
    this.setupCartValidation();
    
    // Setup checkout prevention
    this.setupCheckoutPrevention();
    
    // Also validate when the window loads
    window.addEventListener('load', () => {
      setTimeout(() => this.validateSamplesInCart(), 1000);
    });
  }

  // Debounce function to prevent excessive API calls
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setupCartValidation() {
    // Create debounced validation function to prevent excessive API calls
    this.debouncedValidate = this.debounce(this.validateSamplesInCart.bind(this), 1000);
    
    // Listen for cart updates from external sources
    document.addEventListener('cart:updated', this.debouncedValidate);
    
    // Listen for critical cart events that require immediate validation
    document.addEventListener('theme:cart:change', this.validateSamplesInCart.bind(this));
    document.addEventListener('theme:cart:add', this.validateSamplesInCart.bind(this));
    
    // Listen for cart item removals - IMMEDIATE for security
    document.addEventListener('cart:item:removed', this.validateSamplesInCart.bind(this));
    
    // Listen for cart drawer opens to validate immediately for security
    document.addEventListener('theme:cart-drawer:open', this.validateSamplesInCart.bind(this));
    
    // Listen for page visibility changes to validate when user returns
    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));
    
    // Listen for navigation events
    window.addEventListener('popstate', this.debouncedValidate);
    
    // Listen for cart drawer content updates
    this.observeCartDrawerChanges();
    
    // Reduced frequency - check every 10 seconds instead of 3
    this.cartValidationInterval = setInterval(() => {
      this.debouncedValidate();
    }, 10000);
    
    // Validate immediately on setup
    this.validateSamplesInCart();
    
    // Simplified mutation observer - only watch for significant changes
    this.setupCartMutationObserver();
  }

  observeCartDrawerChanges() {
    // Watch for changes in the cart drawer content
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Listen for when cart items are updated
      cartDrawer.addEventListener('cart-items:updated', this.validateSamplesInCart.bind(this));
      
      // Also watch for when the cart drawer is fully loaded
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if cart items were added/removed
            const hasCartItems = Array.from(mutation.addedNodes).some(node =>
              node.nodeType === Node.ELEMENT_NODE && 
              (node.classList.contains('cart-item') || node.querySelector('.cart-item'))
            );
            
            if (hasCartItems) {
              setTimeout(() => this.validateSamplesInCart(), 100);
            }
          }
        });
      });
      
      observer.observe(cartDrawer, { childList: true, subtree: true });
      this.cartMutationObserver = observer;
    }
  }

  setupCartMutationObserver() {
    // Simplified mutation observer - only watch for significant cart changes
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    
    const observer = new MutationObserver((mutations) => {
      let shouldValidate = false;
      
      mutations.forEach((mutation) => {
        // Only validate on significant changes
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if cart items were actually added/removed
          const hasCartItems = Array.from(mutation.addedNodes).some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            (node.classList?.contains('cart-item') || node.querySelector?.('.cart-item'))
          );
          if (hasCartItems) {
            shouldValidate = true;
          }
        }
      });
      
      if (shouldValidate) {
        this.debouncedValidate();
      }
    });
    
    observer.observe(cartDrawer, { 
      childList: true, 
      subtree: true
    });
    
    this.cartMutationObserver = observer;
  }

  onVisibilityChange() {
    // When user returns to the tab, validate cart immediately
    if (!document.hidden) {
      this.validateSamplesInCart();
    }
  }

  onCartItemRemoved(event) {
    // When a cart item is removed, validate samples immediately for security
    this.validateSamplesInCart();
  }

  async validateSamplesInCart() {
    // Prevent multiple simultaneous validation calls
    if (this.isValidating) return;
    this.isValidating = true;
    
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      const hasPaidProducts = cart.items.some(item => item.product_type !== 'sample');
      const sampleItems = cart.items.filter(item => item.product_type === 'sample');
    
      
      // If no paid products but samples exist, remove all samples IMMEDIATELY
      if (!hasPaidProducts && sampleItems.length > 0) {
        console.log('No paid products found, removing all samples IMMEDIATELY');
        
        // IMMEDIATELY disable checkout to prevent race condition
        this.disableCheckoutButtons();
        
        // Remove samples immediately without any delays
        await this.removeAllSamples();
        
        // Update the form state
        this.refreshFormState();
        
        // Show warning message
        this.showWarningMessage('Samples removed - samples require a paid product in cart');
        
        // Also update the drawer state if it's open
        if (this.samplesDrawer) {
          this.samplesDrawer.showNoPaidProductsWarning();
        }
        
        // Dispatch event to notify other components
        document.dispatchEvent(new CustomEvent('samples:removed', {
          detail: { reason: 'no_paid_products' }
        }));
        
        // Prevent checkout by disabling checkout buttons
        this.disableCheckoutButtons();
        
        // Force a cart refresh to ensure backend is updated
        this.forceCartRefresh();
        
        // CRITICAL: Force cart drawer refresh to update frontend display
        this.forceCartDrawerRefresh();
        
        this.updateTriggerDisplayFromCart();
        
      } else if (hasPaidProducts && sampleItems.length > 0) {
        // If there are paid products and samples, ensure the drawer is enabled
        if (this.samplesDrawer) {
          this.samplesDrawer.hideNoPaidProductsWarning();
        }
        
        // Re-enable checkout buttons
        this.enableCheckoutButtons();
        
        // Update the trigger display to reflect current sample count
        this.updateTriggerDisplayFromCart();
      } else if (!hasPaidProducts && sampleItems.length === 0) {
        // No paid products and no samples - ensure checkout is disabled
        this.disableCheckoutButtons();
        
        // Update the trigger display to reflect no samples
        this.updateTriggerDisplayFromCart();
      }
    } catch (e) {
      console.error('Failed to validate cart:', e);
    } finally {
      this.isValidating = false;
    }
  }

  async forceCartRefresh() {
    try {
      // Force a cart refresh by making a request to the cart page
      const cartResponse = await fetch('/cart.js', { 
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (cartResponse.ok) {
        console.log('Cart refreshed successfully');
        
        // Dispatch a custom event to notify other components
        document.dispatchEvent(new CustomEvent('cart:force-refresh', {
          detail: { timestamp: Date.now() }
        }));
      }
    } catch (e) {
      console.error('Failed to force cart refresh:', e);
    }
  }

  // Force cart drawer refresh to update frontend display
  forceCartDrawerRefresh() {
    try {
      console.log('Forcing cart drawer refresh to update frontend display');
      
      // IMMEDIATELY disable checkout buttons before any cart operations
      this.disableCheckoutButtons();
      
      // Force the cart drawer to refresh its contents
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.getCart === 'function') {
        // Use a small delay to ensure backend cart update is complete
        setTimeout(() => {
          console.log('Executing forced cart drawer refresh');
          cartItems.getCart();
        }, 500);
      }
      
      // Also dispatch a custom event to force cart update
      document.dispatchEvent(new CustomEvent('cart:force-update', {
        detail: { 
          reason: 'security_validation',
          timestamp: Date.now() 
        }
      }));
      
    } catch (e) {
      console.error('Failed to force cart drawer refresh:', e);
    }
  }

  // Enhanced method to handle fresh page loads
  async validateCartOnPageLoad() {
    // Wait a bit for the page to fully load
    setTimeout(async () => {
      await this.validateSamplesInCart();
      
      // Also check if we're on a cart-related page
      if (window.location.pathname.includes('/cart') || 
          window.location.pathname.includes('/checkout') ||
          document.querySelector('cart-drawer')) {
        
        // More aggressive validation on cart pages
        await this.validateSamplesInCart();
        
        // Double-check after a short delay
        setTimeout(() => this.validateSamplesInCart(), 1000);
      }
    }, 500);
  }

  async removeAllSamples() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      const sampleItems = cart.items.filter(item => item.product_type === 'sample');
      
      if (sampleItems.length === 0) return;
      
      // Create updates object to remove all samples
      const updates = {};
      sampleItems.forEach(item => {
        updates[item.variant_id] = 0;
      });
      
      // Remove all samples
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      
      
      this.updateTriggerDisplayFromCart();
    } catch (e) {
      console.error('Failed to remove samples:', e);
    }
  }

  showWarningMessage(message) {
    this.errorMessage.textContent = message;
    this.errorMessageWrapper.classList.remove('hidden');
    setTimeout(() => {
      this.errorMessageWrapper.classList.add('hidden');
    }, 5000);
  }

  connectedCallback() {
    this.maxSelections = this.getAttribute('max-selections') || 2;
    
    document.addEventListener('shopify:cart:updated', this.onCartUpdate.bind(this));
  }

  async init() {
    this.checkboxes.forEach((cb) => {
      cb.addEventListener('change', this.onCheckboxChange.bind(this));
    });

    this.submitButton.addEventListener('click', this.onSubmit.bind(this));
    
    // Initialize checkbox states and button styles
    await this.refreshFormState();
    
    // Enhanced validation for page loads
    this.validateCartOnPageLoad();
    
    // Also validate when the page becomes visible
    if (document.visibilityState === 'visible') {
      this.validateSamplesInCart();
    }
  }

  // Add checkout prevention
  setupCheckoutPrevention() {
    // Prevent navigation to checkout with invalid cart
    document.addEventListener('click', this.preventInvalidCheckout.bind(this));
    
    // Also prevent form submissions to checkout
    document.addEventListener('submit', this.preventInvalidCheckout.bind(this));
    
    // AGGRESSIVE: Intercept checkout clicks at the DOM level
    this.setupAggressiveCheckoutPrevention();
  }

  // Aggressive checkout prevention that works immediately
  setupAggressiveCheckoutPrevention() {
    // Watch for checkout buttons and disable them immediately if cart is invalid
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if this is a checkout button
              const checkoutButtons = node.querySelectorAll && node.querySelectorAll('[data-checkout], .checkout-button, .btn--checkout, [href*="/checkout"]');
              if (checkoutButtons && checkoutButtons.length > 0) {
                // Immediately validate cart and disable if needed
                this.validateSamplesInCart().then(() => {
                  if (this.isCartInvalid()) {
                    checkoutButtons.forEach(button => this.disableCheckoutButton(button));
                  }
                });
              }
              
              // Also check if the node itself is a checkout button
              if (node.matches && node.matches('[data-checkout], .checkout-button, .btn--checkout, [href*="/checkout"]')) {
                this.validateSamplesInCart().then(() => {
                  if (this.isCartInvalid()) {
                    this.disableCheckoutButton(node);
                  }
                });
              }
            }
          });
        }
      });
    });
    
    // Start observing
    observer.observe(document.body, { childList: true, subtree: true });
    this.checkoutObserver = observer;
  }

  // Disable a single checkout button immediately
  disableCheckoutButton(button) {
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('disabled');
    button.style.pointerEvents = 'none';
    button.style.opacity = '0.5';
    
    // Store original text and replace with warning
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent || button.innerText;
    }
    if (button.tagName === 'A') {
      button.href = 'javascript:void(0)';
    }
    button.textContent = 'Add a product to checkout';
  }

  preventInvalidCheckout(event) {
    const target = event.target;
    const isCheckoutAction = target.matches('[data-checkout], .checkout-button, .btn--checkout') ||
                            target.closest('[data-checkout], .checkout-button, .btn--checkout') ||
                            target.form?.action?.includes('/checkout') ||
                            target.href?.includes('/checkout');
    
    if (isCheckoutAction) {
      // Check if cart is valid before allowing checkout
      this.validateSamplesInCart().then(() => {
        // If validation fails, prevent the action
        if (this.isCartInvalid()) {
          event.preventDefault();
          event.stopPropagation();
          
          this.showWarningMessage('Cannot checkout with only samples. Please add a paid product.');
          
          // Disable the checkout button
          if (target.tagName === 'BUTTON' || target.tagName === 'A') {
            target.setAttribute('aria-disabled', 'true');
            target.classList.add('disabled');
            target.style.pointerEvents = 'none';
            target.style.opacity = '0.5';
          }
          
          return false;
        }
      });
    }
  }

  isCartInvalid() {
    // Check if cart has only samples (no paid products)
    const cartItems = document.querySelectorAll('[data-cart-item], .cart-item');
    let hasPaidProducts = false;
    let hasSamples = false;
    
    cartItems.forEach(item => {
      const productType = item.dataset.productType || item.getAttribute('data-product-type');
      if (productType === 'sample') {
        hasSamples = true;
      } else {
        hasPaidProducts = true;
      }
    });
    
    return hasSamples && !hasPaidProducts;
  }

  async refreshFormState() {
    try {
      // Get current cart to sync checkbox states
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      // Find sample items in the cart
      const sampleItems = cart.items.filter(
        (item) => item.product_type === 'sample'
      );
      
      // Update checkbox states based on cart
      this.checkboxes.forEach((checkbox) => {
        const productId = checkbox.dataset.productId;
        const isInCart = sampleItems.some(item => item.product_id.toString() === productId);
        checkbox.checked = isInCart;
      });
      
      // Update UI states
      this.updateCheckboxStates();
      this.updateButtonStyles();
    } catch (e) {
      console.error('Failed to refresh form state:', e);
    }
  }

  // Method to be called when cart is updated externally
  onCartUpdate() {
    this.refreshFormState();
  }

  getSelectedProductIds() {
    return Array.from(this.checkboxes)
      .filter((cb) => cb.checked)
      .map((cb) => cb.dataset.productId);
  }

  updateCheckboxStates() {
    const selectedCount = this.getSelectedProductIds().length;
    const maxSelections = Number(this.maxSelections);
    
    this.checkboxes.forEach((checkbox) => {
      const isDisabled = selectedCount >= maxSelections && !checkbox.checked;
      checkbox.disabled = isDisabled;
      
      // Update the custom checkbox styling
      const customCheckbox = checkbox.nextElementSibling;
      if (customCheckbox && customCheckbox.classList.contains('checkmark')) {
        customCheckbox.style.opacity = isDisabled ? '0.5' : '1';
        customCheckbox.style.cursor = isDisabled ? 'not-allowed' : 'pointer';
      }
    });
  }

  updateButtonStyles() {
    const selectedCount = this.getSelectedProductIds().length;
    const maxSelections = Number(this.maxSelections);
    
    // Update button style based on selection count
    const isAtMax = selectedCount >= maxSelections;
    this.submitButton.classList.toggle('samples-drawer__button--primary', isAtMax);
    this.submitButton.classList.toggle('samples-drawer__button--secondary', !isAtMax);
    
    // Update button text
    const buttonText = this.submitButton.querySelector('span');
    if (buttonText) {
      if (selectedCount === 0) {
        buttonText.textContent = 'Select Samples';
      } else if (selectedCount === 1) {
        buttonText.textContent = `Add 1 Sample (${maxSelections - selectedCount} remaining)`;
      } else {
        buttonText.textContent = 'Confirm Samples';
      }
    }
  }

  updateTriggerDisplay() {
    // This method should only be called after actual cart changes, not during selection
    if (this.samplesDrawerTrigger) {
      const selectedCount = this.getSelectedProductIds().length;
      const maxSelections = Number(this.maxSelections);
      
      // Update the trigger badge
      const badge = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-badge');
      if (badge) {
        badge.textContent = selectedCount;
        badge.style.display = selectedCount > 0 ? 'inline-block' : 'none';
      }
      
      // Update trigger text
      const triggerTitle = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-title');
      const triggerDesc = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-desc');
      const triggerButton = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-button');
      
      if (triggerTitle && triggerDesc) {
        if (selectedCount === 0) {
          triggerTitle.textContent = 'Free Samples';
          triggerDesc.textContent = `Choose up to ${maxSelections} samples`;
        } else if (selectedCount === 1) {
          triggerTitle.textContent = `${selectedCount} Sample Selected`;
          triggerDesc.textContent = `You have selected 1 free sample`;
        } else if (selectedCount === 2) {
          triggerTitle.textContent = `${selectedCount} Samples Selected`;
          triggerDesc.textContent = 'You have selected 2 free samples';
        } else {
          triggerTitle.textContent = `${selectedCount} Samples Selected`;
          triggerDesc.textContent = `${maxSelections - selectedCount} remaining`;
        }
      }
      
      // Update trigger button text
      if (triggerButton) {
        if (selectedCount === 0) {
          triggerButton.textContent = 'Select Samples';
        } else {
          triggerButton.textContent = 'Edit Samples';
        }
      }
      
    }
  }

  // New method that only updates trigger display based on actual cart state
  async updateTriggerDisplayFromCart() {
    try {
      // Cache cart state to avoid unnecessary API calls
      if (this.cachedCartState && Date.now() - this.cachedCartState.timestamp < 2000) {
        this.updateTriggerDisplayUI(this.cachedCartState.sampleCount);
        return;
      }
      
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      const sampleItems = cart.items.filter(item => item.product_type === 'sample');
      const selectedCount = sampleItems.length;
      
      // Cache the result
      this.cachedCartState = {
        sampleCount: selectedCount,
        timestamp: Date.now()
      };
      
      this.updateTriggerDisplayUI(selectedCount);
    } catch (e) {
      console.error('Failed to update trigger display from cart:', e);
    }
  }

  updateTriggerDisplayUI(selectedCount) {
    const maxSelections = Number(this.maxSelections);
    
    if (this.samplesDrawerTrigger) {
      // Update the trigger badge
      const badge = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-badge');
      if (badge) {
        badge.textContent = selectedCount;
        badge.style.display = selectedCount > 0 ? 'inline-block' : 'none';
      }
      
      // Update trigger text
      const triggerTitle = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-title');
      const triggerDesc = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-desc');
      const triggerButton = this.samplesDrawerTrigger.querySelector('.samples-drawer__trigger-button');
      
      if (triggerTitle && triggerDesc) {
        if (selectedCount === 0) {
          triggerTitle.textContent = 'Free Samples';
          triggerDesc.textContent = `Choose up to ${maxSelections} samples`;
        } else if (selectedCount === 1) {
          triggerTitle.textContent = `${selectedCount} Sample Selected`;
          triggerDesc.textContent = `You have selected 1 free sample`;
        } else if (selectedCount === 2) {
          triggerTitle.textContent = `${selectedCount} Samples Selected`;
          triggerDesc.textContent = 'You have selected 2 free samples';
        } else {
          triggerTitle.textContent = `${selectedCount} Samples Selected`;
          triggerDesc.textContent = `${maxSelections - selectedCount} remaining`;
        }
      }
      
      // Update trigger button text
      if (triggerButton) {
        if (selectedCount === 0) {
          triggerButton.textContent = 'Select Samples';
        } else {
          triggerButton.textContent = 'Edit Samples';
        }
      }
    }
  }

  onCheckboxChange(e) {
    const selectedCount = this.getSelectedProductIds().length;
    const maxSelections = Number(this.maxSelections);

    // Handle over-selection
    if (selectedCount > maxSelections) {
      e.target.checked = false;
      this.errorMessage.textContent = `You can only select ${this.maxSelections} sample(s) at a time.`;
      this.handleErrorMessage();
      return;
    }

    // Update checkbox states and button styles
    this.updateCheckboxStates();
    this.updateButtonStyles();
  }

  handleErrorMessage(message) {
    if (message) {
      this.errorMessage.textContent = message;
    } else {
      this.errorMessage.textContent = `You can only have ${this.maxSelections} unique samples.`;
    }
    this.errorMessageWrapper.classList.remove('hidden');
    setTimeout(() => {
      this.errorMessageWrapper.classList.add('hidden');
    }, this.errorMessageTimeout);
  }

  async onSubmit(e) {
    e.preventDefault();

    const selectedItems = Array.from(this.checkboxes)
      .filter((cb) => cb.checked)
      .map((cb) => ({
        id: cb.value,
        quantity: 1
      }));

    // Check if the selected items exceed the max selections
    if (selectedItems.length > this.maxSelections) {
      this.handleErrorMessage();
      return;
    }

    // Validate that there are paid products in cart before allowing samples
    const cartValidation = await this.validateCartBeforeSamples();
    if (!cartValidation.isValid) {
      this.showWarningMessage(cartValidation.message);
      return;
    }

    this.setLoading(true);

    try {
      // Get the current cart items
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      // Find sample items in the cart
      const sampleItems = cart.items.filter(
        (item) => item.product_type === 'sample'
      );
      
      // Create updates object to modify cart
      const updates = {};
      
      // Set all existing sample items to quantity 0 (remove them)
      sampleItems.forEach((item) => {
        updates[item.variant_id] = 0;
      });
      
      // Add the selected items to the updates object
      selectedItems.forEach((item) => {
        updates[item.id] = item.quantity;
      });
      
      // Send the updates to the cart
      const updateResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      
      const updateResult = await updateResponse.json();
      
      if (updateResult.status) {
        this.handleErrorMessage(updateResult.description);
        return;
      }

      // Refresh the cart drawer contents
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.getCart === 'function') {
        cartItems.getCart();
      }
      
      // Update the trigger display after successful cart update
      this.updateTriggerDisplayFromCart();
      
      // Close the samples drawer after successful confirmation
      if (this.samplesDrawer) {
        this.samplesDrawer.toggleOpen();
      }
      
    } catch (e) {
      console.error(e);
      this.handleErrorMessage('Failed to update cart');
    } finally {
      this.setLoading(false);
    }
  }

  async validateCartBeforeSamples() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      // Check if there are any paid products (non-samples)
      const paidProducts = cart.items.filter(item => item.product_type !== 'sample');
      
      if (paidProducts.length === 0) {
        return {
          isValid: false,
          message: 'Cannot add samples without a paid product in cart'
        };
      }
      
      return { isValid: true };
    } catch (e) {
      console.error('Failed to validate cart:', e);
      return {
        isValid: false,
        message: 'Failed to validate cart'
      };
    }
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      this.spinner?.classList.remove('hidden');
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('loading');
      this.spinner?.classList.add('hidden');
    }
  }

  disableCheckoutButtons() {
    // Disable all checkout buttons when cart is invalid
    const checkoutButtons = document.querySelectorAll('[data-checkout], .checkout-button, .btn--checkout, [href*="/checkout"]');
    checkoutButtons.forEach(button => {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('disabled');
      button.style.pointerEvents = 'none';
      button.style.opacity = '0.5';
      
      // Store original text and replace with warning
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent || button.innerText;
      }
      if (button.tagName === 'A') {
        button.href = 'javascript:void(0)';
      }
      button.textContent = 'Add a product to checkout';
    });
  }

  enableCheckoutButtons() {
    // Re-enable all checkout buttons when cart becomes valid
    const checkoutButtons = document.querySelectorAll('[data-checkout], .checkout-button, .btn--checkout, [href*="/checkout"]');
    checkoutButtons.forEach(button => {
      button.removeAttribute('aria-disabled');
      button.classList.remove('disabled');
      button.style.pointerEvents = '';
      button.style.opacity = '';
      
      // Restore original text
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
        delete button.dataset.originalText;
      }
      
      // Restore original href for links
      if (button.tagName === 'A' && button.dataset.originalHref) {
        button.href = button.dataset.originalHref;
        delete button.dataset.originalHref;
      }
    });
  }

  // Cleanup method to prevent memory leaks
  disconnectedCallback() {
    if (this.cartValidationInterval) {
      clearInterval(this.cartValidationInterval);
    }
    document.removeEventListener('cart:updated', this.validateSamplesInCart.bind(this));
    document.removeEventListener('shopify:cart:updated', this.onCartUpdate.bind(this));
    if (this.cartMutationObserver) {
      this.cartMutationObserver.disconnect();
    }
    if (this.cartMutationObservers) {
      this.cartMutationObservers.forEach(observer => observer.disconnect());
    }
    if (this.checkoutObserver) {
      this.checkoutObserver.disconnect();
    }
  }
}

if (!customElements.get('samples-form')) {
  customElements.define('samples-form', SamplesForm);
}
